import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'

const extractId = (data, key, objectKey) => {
  const direct = data[key]
  if (direct !== undefined && direct !== null && direct !== '') return direct
  const obj = data[objectKey || key.replace(/_id$/, '')]
  return obj && typeof obj === 'object' && obj.id !== undefined ? obj.id : null
}

const coalesce = (...values) => {
  for (const v of values) {
    if (v != null && v !== undefined) return v
  }
  return values[values.length - 1]
}

const hasValue = (value) => {
  if (value === undefined || value === null) return false
  if (typeof value === 'string' && value.trim() === '') return false
  return true
}

export function validatePolicyExceptionBeforeReview(data, catalogs = {}) {
  const scopeTypes = catalogs.scope_types || []
  const getScopeName = (scopeTypeId) => {
    const found = scopeTypes.find((s) => s.id === scopeTypeId || s.id === Number(scopeTypeId))
    return found ? String(found.name || '').toLowerCase() : ''
  }
  const missing = []

  if (!hasValue(data.reference_number)) missing.push({ name: 'reference_number' })
  const scopeTypeId = extractId(data, 'scope_type_id', 'scope_type')
  if (!hasValue(scopeTypeId)) missing.push({ name: 'scope_type' })
  if (!hasValue(extractId(data, 'request_type_id', 'request_type'))) missing.push({ name: 'request_type' })
  if (!hasValue(extractId(data, 'exception_type_id', 'exception_type'))) missing.push({ name: 'exception_type' })
  if (!hasValue(extractId(data, 'source_id', 'source'))) missing.push({ name: 'source' })
  if (!hasValue(extractId(data, 'inherent_risk_id', 'inherent_risk'))) missing.push({ name: 'inherent_risk' })
  if (!hasValue(extractId(data, 'residual_risk_id', 'residual_risk'))) missing.push({ name: 'residual_risk' })
  const exceptionTypesForStandards = catalogs.exception_types || []
  const exceptionTypeIdForStandards = extractId(data, 'exception_type_id', 'exception_type')
  const exceptionTypeFoundForStandards = exceptionTypesForStandards.find((e) => e.id === exceptionTypeIdForStandards || e.id === Number(exceptionTypeIdForStandards))
  const exceptionTypeNameForStandards = exceptionTypeFoundForStandards ? String(exceptionTypeFoundForStandards.name || '').toLowerCase() : ''
  const isStandardsRequired = exceptionTypeNameForStandards === 'excepcion_politica' || exceptionTypeNameForStandards.includes('politica')
  if (isStandardsRequired) {
    const standardIds = data.standard_ids || (data.standards && data.standards.map((s) => (typeof s === 'object' ? (s.standard_id ?? s.standard?.id ?? s.id) : s))) || []
    if (!Array.isArray(standardIds) || standardIds.length === 0) missing.push({ name: 'standards' })
  }
  if (!hasValue(data.description)) missing.push({ name: 'description' })
  if (!hasValue(data.compensatory_measures)) missing.push({ name: 'compensatory_measures' })
  if (!hasValue(data.justification)) missing.push({ name: 'justification' })
  if (!hasValue(data.request_date)) missing.push({ name: 'request_date' })
  if (!hasValue(data.start_date)) missing.push({ name: 'start_date' })
  if (!hasValue(data.end_date)) missing.push({ name: 'end_date' })

  const scopeName = getScopeName(scopeTypeId)
  if (scopeName.includes('inmueble')) {
    const unitIds = data.unit_ids || (data.units && data.units.map((u) => (typeof u === 'object' ? u.id : u))) || []
    if (!Array.isArray(unitIds) || unitIds.length === 0) missing.push({ name: 'units' })
  }
  if (scopeName.includes('region')) {
    const sectorIds = data.sector_ids || (data.sectors && data.sectors.map((s) => (typeof s === 'object' ? s.id : s))) || []
    if (!Array.isArray(sectorIds) || sectorIds.length === 0) missing.push({ name: 'sectors' })
  }

  const regulatoryRequired = data.regulatory_required === true
  if (regulatoryRequired && !hasValue(data.regulation_description)) {
    missing.push({ name: 'regulation_description' })
  }

  const exceptionTypes = catalogs.exception_types || []
  const exceptionTypeId = extractId(data, 'exception_type_id', 'exception_type')
  const exceptionTypeFound = exceptionTypes.find((e) => e.id === exceptionTypeId || e.id === Number(exceptionTypeId))
  const exceptionTypeName = exceptionTypeFound ? String(exceptionTypeFound.name || '').toLowerCase() : ''
  const isSectionDetailRequired = exceptionTypeName === 'excepcion_politica' || exceptionTypeName.includes('politica')
  if (isSectionDetailRequired && !hasValue(data.section_detail)) {
    missing.push({ name: 'section_detail' })
  }

  const requestDate = data.request_date ? new Date(data.request_date) : null
  const startDate = data.start_date ? new Date(data.start_date) : null
  const endDate = data.end_date ? new Date(data.end_date) : null
  if (requestDate && startDate && startDate < requestDate) {
    missing.push({ name: 'start_date', message: 'start_date_must_be_after_request_date' })
  }
  if (startDate && endDate && endDate < startDate) {
    missing.push({ name: 'end_date', message: 'end_date_must_be_after_start_date' })
  }
  if (requestDate && endDate && endDate <= requestDate) {
    missing.push({ name: 'end_date', message: 'end_date_must_be_after_request_date' })
  }

  return missing.length ? { ok: false, missing } : { ok: true, missing: [] }
}

export function validatePolicyExceptionForSave(data) {
  const missing = []
  if (!hasValue(data.reference_number)) missing.push({ name: 'reference_number' })
  const scopeTypeId = extractId(data, 'scope_type_id', 'scope_type')
  if (!hasValue(scopeTypeId)) missing.push({ name: 'scope_type' })
  return missing.length ? { ok: false, missing } : { ok: true, missing: [] }
}

const FIELD_NAME_TO_I18N_KEY = {
  reference_number: 'policy_exception.reference_number',
  scope_type: 'policy_exception.scope_type',
  request_type: 'policy_exception.request_type',
  exception_type: 'policy_exception.exception_type',
  source: 'policy_exception.source',
  inherent_risk: 'policy_exception.inherent_risk',
  residual_risk: 'policy_exception.residual_risk',
  standards: 'policy_exception.standards_affected',
  description: 'policy_exception.description',
  compensatory_measures: 'policy_exception.compensatory_measures',
  justification: 'policy_exception.justification',
  section_detail: 'policy_exception.section_detail',
  units: 'policy_exception.units',
  sectors: 'policy_exception.sectors',
  regulation_description: 'policy_exception.regulation_description',
  request_date: 'policy_exception.request_date',
  start_date: 'policy_exception.start_date',
  end_date: 'policy_exception.end_date',
}

export function getFieldLabelKey(fieldName) {
  return FIELD_NAME_TO_I18N_KEY[fieldName] || fieldName
}

function generateDefaultReferenceNumber() {
  const yearShort = String(new Date().getFullYear()).slice(-2)
  const suffix = String(Date.now()).slice(-6)
  return `EP-${yearShort}-${suffix}`
}

function toDateOnly(val) {
  return val ? String(val).slice(0, 10) : ''
}

function getTodayDateString() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function resolveInProgressStatusId(catalogs = {}) {
  const statuses = catalogs.statuses || []
  const found = statuses.find(
    (s) => String((s && s.name) || '').toLowerCase().replace(/\s/g, '_') === 'in_progress'
  )
  return found && found.id != null ? found.id : null
}

function getDefaultRequestDate(initialData) {
  const value = initialData.request_date || ''
  if (value) return toDateOnly(value)
  return getTodayDateString()
}

function normalizeAttachments(attachments) {
  if (!Array.isArray(attachments)) return []
  return attachments
    .filter((a) => a && (a.url || a.name))
    .map((a) => ({ url: a.url || '', name: a.name || '' }))
}

function getDefaultStartDate(initialData) {
  const value = initialData.start_date || ''
  if (value) return toDateOnly(value)
  return ''
}

export default function usePolicyExceptionForm(initialData = {}, catalogsRef = ref({})) {
  const formData = ref({
    reference_number: initialData.reference_number || generateDefaultReferenceNumber(),
    scope_type_id: coalesce(initialData.scope_type_id, (initialData.scope_type && initialData.scope_type.id), null),
    request_type_id: coalesce(initialData.request_type_id, (initialData.request_type && initialData.request_type.id), null),
    exception_type_id: coalesce(initialData.exception_type_id, (initialData.exception_type && initialData.exception_type.id), null),
    status_id: coalesce(initialData.status_id, (initialData.status && initialData.status.id), null),
    source_id: coalesce(initialData.source_id, (initialData.source && initialData.source.id), null),
    inherent_risk_id: coalesce(initialData.inherent_risk_id, (initialData.inherent_risk && initialData.inherent_risk.id), null),
    residual_risk_id: coalesce(initialData.residual_risk_id, (initialData.residual_risk && initialData.residual_risk.id), null),
    regulatory_required: initialData.regulatory_required === true,
    regulation_description: initialData.regulation_description || '',
    standard_ids: initialData.standard_ids || (initialData.standards && initialData.standards.map((s) => (typeof s === 'object' ? (s.standard_id ?? s.standard?.id ?? s.id) : s))) || [],
    unit_ids: initialData.unit_ids || (initialData.units && initialData.units.map((u) => u.id)) || [],
    sector_ids: initialData.sector_ids || (initialData.sectors && initialData.sectors.map((s) => (typeof s === 'object' ? s.id : s))) || [],
    description: initialData.description || '',
    compensatory_measures: initialData.compensatory_measures || '',
    justification: initialData.justification || '',
    section_detail: initialData.section_detail || '',
    section_attachments: normalizeAttachments(initialData.section_attachments),
    comments: initialData.comments || '',
    approver_soeid: initialData.approver_soeid || '',
    request_date: getDefaultRequestDate(initialData),
    start_date: getDefaultStartDate(initialData),
    end_date: initialData.end_date ? toDateOnly(initialData.end_date) : '',
    attachments: normalizeAttachments(initialData.attachments),
  })

  const dispatchSafely = (action) => {
    try {
      store.dispatch(action)
    } catch (_) {}
  }
  dispatchSafely('unit/getAll')
  dispatchSafely('region/getAll')
  dispatchSafely('sector/getAll')
  dispatchSafely('user/getAll')

  const getStoreItems = (getter) => {
    try {
      return store.getters[getter] || []
    } catch (_) {
      return []
    }
  }
  const formatUnitOptionLabel = (description, sirh, rems) => {
    const desc = description != null && description !== '' ? String(description) : ''
    const sirhDisplay = sirh != null && sirh !== '' ? String(sirh) : '-'
    const remsDisplay = rems != null && rems !== '' ? String(rems) : '-'
    const suffix = `SIRH: ${sirhDisplay} | REMS: ${remsDisplay}`
    return desc ? `${desc} | ${suffix}` : suffix
  }
  const unitOptions = computed(() => {
    const items = getStoreItems('unit/getSelectedItems') || []
    return items.map((item) => ({
      ...item,
      label: formatUnitOptionLabel(item.label, item.sirh, item.rems),
    }))
  })
  const sectorOptions = computed(() => getStoreItems('sector/getSelectedItems'))

  const getScopeNameById = (scopeTypeId) => {
    const catalogs = catalogsRef && catalogsRef.value ? catalogsRef.value : {}
    const scopeTypes = catalogs.scope_types || []
    const found = scopeTypes.find((s) => s.id === scopeTypeId || s.id === Number(scopeTypeId))
    return found ? String(found.name || '').toLowerCase() : ''
  }

  const showUnitsSelector = computed(() => {
    const name = getScopeNameById(formData.value.scope_type_id)
    return name.includes('inmueble')
  })

  const showSectorsSelector = computed(() => {
    const name = getScopeNameById(formData.value.scope_type_id)
    return name.includes('region')
  })

  const showRegulationDescription = computed(() => !!formData.value.regulatory_required)

  const isSectionDetailEnabled = computed(() => {
    const catalogs = catalogsRef && catalogsRef.value ? catalogsRef.value : {}
    const exceptionTypes = catalogs.exception_types || []
    const exceptionTypeId = formData.value.exception_type_id
    const found = exceptionTypes.find((e) => e.id === exceptionTypeId || e.id === Number(exceptionTypeId))
    const name = found ? String(found.name || '').toLowerCase() : ''
    return name === 'excepcion_politica' || name.includes('politica')
  })

  const showStandardsAffected = computed(() => {
    const catalogs = catalogsRef && catalogsRef.value ? catalogsRef.value : {}
    const exceptionTypes = catalogs.exception_types || []
    const exceptionTypeId = formData.value.exception_type_id
    const found = exceptionTypes.find((e) => e.id === exceptionTypeId || e.id === Number(exceptionTypeId))
    const name = found ? String(found.name || '').toLowerCase() : ''
    return name === 'excepcion_politica' || name.includes('politica')
  })

  watch(
    () => formData.value.scope_type_id,
    () => {
      const name = getScopeNameById(formData.value.scope_type_id)
      if (name.includes('nacional')) {
        formData.value.unit_ids = []
        formData.value.sector_ids = []
      }
    }
  )

  watch(
    () => formData.value.exception_type_id,
    () => {
      const catalogs = catalogsRef && catalogsRef.value ? catalogsRef.value : {}
      const exceptionTypes = catalogs.exception_types || []
      const exceptionTypeId = formData.value.exception_type_id
      const found = exceptionTypes.find((e) => e.id === exceptionTypeId || e.id === Number(exceptionTypeId))
      const name = found ? String(found.name || '').toLowerCase() : ''
      const isPolitica = name === 'excepcion_politica' || name.includes('politica')
      if (!isPolitica) formData.value.standard_ids = []
    }
  )

  watch(
    () => formData.value.regulatory_required,
    (enabled) => {
      if (!enabled) formData.value.regulation_description = ''
    }
  )

  const validateDates = () => {
    const errors = []
    const requestDate = formData.value.request_date ? new Date(formData.value.request_date) : null
    const startDate = formData.value.start_date ? new Date(formData.value.start_date) : null
    const endDate = formData.value.end_date ? new Date(formData.value.end_date) : null

    if (requestDate && startDate && startDate < requestDate) {
      errors.push({ field: 'start_date', message: 'start_date_must_be_after_request_date' })
    }
    if (startDate && endDate && endDate < startDate) {
      errors.push({ field: 'end_date', message: 'end_date_must_be_after_start_date' })
    }
    if (requestDate && endDate && endDate <= requestDate) {
      errors.push({ field: 'end_date', message: 'end_date_must_be_after_request_date' })
    }
    return errors
  }

  const resetForm = (data = {}) => {
    formData.value = {
      reference_number: data.reference_number || generateDefaultReferenceNumber(),
      scope_type_id: coalesce(data.scope_type_id, (data.scope_type && data.scope_type.id), null),
      request_type_id: coalesce(data.request_type_id, (data.request_type && data.request_type.id), null),
      exception_type_id: coalesce(data.exception_type_id, (data.exception_type && data.exception_type.id), null),
      status_id: coalesce(data.status_id, (data.status && data.status.id), null),
      source_id: coalesce(data.source_id, (data.source && data.source.id), null),
      inherent_risk_id: coalesce(data.inherent_risk_id, (data.inherent_risk && data.inherent_risk.id), null),
      residual_risk_id: coalesce(data.residual_risk_id, (data.residual_risk && data.residual_risk.id), null),
      regulatory_required: data.regulatory_required === true,
      regulation_description: data.regulation_description || '',
      standard_ids: (data.standard_ids || (data.standards && data.standards.map((s) => (typeof s === 'object' ? (s.standard_id ?? s.standard?.id ?? s.id) : s))) || []).map((id) => Number(id)),
      unit_ids: data.unit_ids || (data.units && data.units.map((u) => u.id)) || [],
      sector_ids: data.sector_ids || (data.sectors && data.sectors.map((s) => (typeof s === 'object' ? s.id : s))) || [],
      description: data.description || '',
      compensatory_measures: data.compensatory_measures || '',
      justification: data.justification || '',
      section_detail: data.section_detail || '',
      section_attachments: normalizeAttachments(data.section_attachments),
      comments: data.comments || '',
      approver_soeid: data.approver_soeid || '',
      request_date: toDateOnly(data.request_date) || getTodayDateString(),
      start_date: toDateOnly(data.start_date) || '',
      end_date: toDateOnly(data.end_date) || '',
      attachments: normalizeAttachments(data.attachments),
    }
  }

  const validateBeforeReview = (dataToValidate = null) => {
    const data = dataToValidate || formData.value
    const catalogs = catalogsRef && catalogsRef.value ? catalogsRef.value : {}
    return validatePolicyExceptionBeforeReview(data, catalogs)
  }

  const validateForSave = (dataToValidate = null) => {
    const data = dataToValidate || formData.value
    return validatePolicyExceptionForSave(data)
  }

  return {
    formData,
    unitOptions,
    sectorOptions,
    showUnitsSelector,
    showSectorsSelector,
    showRegulationDescription,
    isSectionDetailEnabled,
    showStandardsAffected,
    validateDates,
    validateBeforeReview,
    validateForSave,
    resetForm,
  }
}
